import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const formatCurrency = (value, currency = 'BRL') => {
  return new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'pt-BR', {
    style: 'currency',
    currency: currency
  }).format(value);
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('pt-BR');
};

// Modal Nova Transação - Compra Dólar
export function NovaTransacaoCompraDolarModal({ onClose, onSuccess, editingTransaction }) {
  const [tipoOperacao, setTipoOperacao] = useState(editingTransaction?.tipo_operacao || 'Compra');
  
  // Estados para Compra
  const [valorReais, setValorReais] = useState(editingTransaction?.valor_reais || '');
  const [cotacaoCompra, setCotacaoCompra] = useState(editingTransaction?.cotacao_compra || '');
  const [iofCompra, setIofCompra] = useState(0);
  const [dolaresAdquiridos, setDolaresAdquiridos] = useState(0);
  
  // Estados para Venda
  const [dolaresVendidos, setDolaresVendidos] = useState(editingTransaction?.dolares_vendidos || '');
  const [cotacaoVenda, setCotacaoVenda] = useState(editingTransaction?.cotacao_venda || '');
  const [iofVenda, setIofVenda] = useState(0);
  const [reaisRecebidos, setReaisRecebidos] = useState(0);
  
  const [data, setData] = useState(editingTransaction?.data || new Date().toISOString().split('T')[0]);

  // Cálculos para Compra
  useEffect(() => {
    if (tipoOperacao === 'Compra' && valorReais && cotacaoCompra) {
      const valor = parseFloat(valorReais);
      const cotacao = parseFloat(cotacaoCompra);
      const iof = valor * 0.011;
      const dolares = (valor - iof) / cotacao;
      
      setIofCompra(iof);
      setDolaresAdquiridos(dolares);
    } else {
      setIofCompra(0);
      setDolaresAdquiridos(0);
    }
  }, [valorReais, cotacaoCompra, tipoOperacao]);

  // Cálculos para Venda
  useEffect(() => {
    if (tipoOperacao === 'Venda' && dolaresVendidos && cotacaoVenda) {
      const dolares = parseFloat(dolaresVendidos);
      const cotacao = parseFloat(cotacaoVenda);
      const iof = (dolares * cotacao) * 0.011;
      const reais = (dolares * cotacao) - iof;
      
      setIofVenda(iof);
      setReaisRecebidos(reais);
    } else {
      setIofVenda(0);
      setReaisRecebidos(0);
    }
  }, [dolaresVendidos, cotacaoVenda, tipoOperacao]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      tipo_operacao: tipoOperacao,
      data: data
    };

    if (tipoOperacao === 'Compra') {
      payload.valor_reais = parseFloat(valorReais);
      payload.cotacao_compra = parseFloat(cotacaoCompra);
    } else {
      payload.dolares_vendidos = parseFloat(dolaresVendidos);
      payload.cotacao_venda = parseFloat(cotacaoVenda);
    }

    try {
      if (editingTransaction) {
        await axios.put(`${API}/compra-dolar/transacoes/${editingTransaction.id}`, payload, getAuthHeaders());
        toast.success('Transação atualizada com sucesso');
      } else {
        await axios.post(`${API}/compra-dolar/transacoes`, payload, getAuthHeaders());
        toast.success('Transação criada com sucesso');
      }
      onSuccess();
    } catch (error) {
      toast.error('Erro ao salvar transação');
      console.error(error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-900">
            {editingTransaction ? 'Editar' : 'Nova'} Transação - Compra Dólar
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Tipo de Operação */}
          {!editingTransaction && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Tipo de Operação *
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setTipoOperacao('Compra')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    tipoOperacao === 'Compra'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="font-semibold">Compra de Dólar</div>
                  <div className="text-xs mt-1">Converter Reais em Dólares</div>
                </button>
                <button
                  type="button"
                  onClick={() => setTipoOperacao('Venda')}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    tipoOperacao === 'Venda'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="font-semibold">Venda de Dólar</div>
                  <div className="text-xs mt-1">Converter Dólares em Reais</div>
                </button>
              </div>
            </div>
          )}

          {/* Data */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data da Operação *
            </label>
            <input
              type="date"
              value={data}
              onChange={(e) => setData(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {tipoOperacao === 'Compra' ? (
            <>
              {/* Valor em Reais */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valor Investido em Reais (R$) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={valorReais}
                  onChange={(e) => setValorReais(e.target.value)}
                  required
                  placeholder="Ex: 1000.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Cotação Compra */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cotação do Dólar na Compra *
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={cotacaoCompra}
                  onChange={(e) => setCotacaoCompra(e.target.value)}
                  required
                  placeholder="Ex: 5.25"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* IOF Compra (não editável) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IOF (1,1% automático)
                </label>
                <input
                  type="text"
                  value={formatCurrency(iofCompra, 'BRL')}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>

              {/* Dólares Adquiridos (não editável) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantidade de Dólares Comprados
                </label>
                <input
                  type="text"
                  value={formatCurrency(dolaresAdquiridos, 'USD')}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>
            </>
          ) : (
            <>
              {/* Dólares Vendidos */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantidade de Dólares Vendidos (US$) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={dolaresVendidos}
                  onChange={(e) => setDolaresVendidos(e.target.value)}
                  required
                  placeholder="Ex: 200.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Cotação Venda */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cotação da Venda *
                </label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={cotacaoVenda}
                  onChange={(e) => setCotacaoVenda(e.target.value)}
                  required
                  placeholder="Ex: 5.30"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* IOF Venda (não editável) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IOF (1,1% automático)
                </label>
                <input
                  type="text"
                  value={formatCurrency(iofVenda, 'BRL')}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>

              {/* Reais Recebidos (não editável) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reais Recebidos Após IOF
                </label>
                <input
                  type="text"
                  value={formatCurrency(reaisRecebidos, 'BRL')}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                />
              </div>
            </>
          )}

          <div className="flex gap-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
            >
              {editingTransaction ? 'Atualizar' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Tabela de Transações Compra Dólar
export function CompraDolarTableRows({ transacoes, onEdit, onDelete }) {
  if (!transacoes || transacoes.length === 0) {
    return (
      <tr>
        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
          Nenhuma transação cadastrada
        </td>
      </tr>
    );
  }

  return transacoes.map((transacao) => (
    <tr key={transacao.id} className="hover:bg-gray-50">
      {/* Data */}
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDate(transacao.data)}
      </td>

      {/* Tipo */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          transacao.tipo_operacao === 'Compra' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
        }`}>
          {transacao.tipo_operacao}
        </span>
      </td>

      {transacao.tipo_operacao === 'Compra' ? (
        <>
          {/* Valor em Reais */}
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            {formatCurrency(transacao.valor_reais, 'BRL')}
          </td>

          {/* Cotação */}
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {formatCurrency(transacao.cotacao_compra, 'BRL')}
          </td>

          {/* IOF */}
          <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
            {formatCurrency(transacao.iof_compra, 'BRL')}
          </td>

          {/* Dólares Adquiridos */}
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
            {formatCurrency(transacao.dolares_adquiridos, 'USD')}
          </td>
        </>
      ) : (
        <>
          {/* Dólares Vendidos */}
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
            {formatCurrency(transacao.dolares_vendidos, 'USD')}
          </td>

          {/* Cotação */}
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {formatCurrency(transacao.cotacao_venda, 'BRL')}
          </td>

          {/* IOF */}
          <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
            {formatCurrency(transacao.iof_venda, 'BRL')}
          </td>

          {/* Reais Recebidos */}
          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
            {formatCurrency(transacao.reais_recebidos, 'BRL')}
          </td>
        </>
      )}

      {/* Ações */}
      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(transacao)}
            className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
            title="Editar"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          
          <button
            onClick={() => onDelete(transacao.id)}
            className="text-red-600 hover:text-red-900 p-1 rounded"
            title="Excluir"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </td>
    </tr>
  ));
}

export function useCompraDolarActions(carregarDados) {
  const handleDelete = async (transacaoId) => {
    if (!window.confirm('Deseja excluir esta transação?')) {
      return;
    }

    try {
      await axios.delete(`${API}/compra-dolar/transacoes/${transacaoId}`, getAuthHeaders());
      toast.success('Transação excluída');
      carregarDados();
    } catch (error) {
      toast.error('Erro ao excluir transação');
      console.error(error);
    }
  };

  return { handleDelete };
}
